import React, { useEffect, useMemo, useState } from 'react';
import { directoryAPI } from './api';
import './gpj-theme.css';
import './Directory.css';

const safeError = (error, fallback) => error.response?.data?.detail || fallback;

const formatValue = (value) => value
  .split('_')
  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
  .join(' ');

const verificationDate = (value) => new Intl.DateTimeFormat('en-CA', {
  year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC'
}).format(new Date(value));

function DirectoryCard({ entry, optionLabels }) {
  return (
    <article className="gpj-directory-card">
      <div className="gpj-directory-card__head">
        <span className="gpj-directory-kind">
          {optionLabels[entry.entry_type] || formatValue(entry.entry_type)}
        </span>
        <h3>{entry.display_name}</h3>
        {entry.organization_name && entry.organization_name !== entry.display_name && (
          <p className="gpj-directory-organization">{entry.organization_name}</p>
        )}
      </div>

      <p className="gpj-directory-summary">{entry.service_summary}</p>

      <dl className="gpj-directory-meta">
        <div>
          <dt>Coverage</dt>
          <dd>
            {entry.regions.map((region) => (
              optionLabels[region] || formatValue(region)
            )).join(' · ')}
          </dd>
        </div>
        {entry.phone && (
          <div>
            <dt>Phone</dt>
            <dd><a href={`tel:${entry.phone}`}>{entry.phone}</a></dd>
          </div>
        )}
      </dl>

      <div className="gpj-directory-tags" aria-label="Services and categories">
        {entry.categories.map((category) => (
          <span key={category}>{optionLabels[category] || formatValue(category)}</span>
        ))}
      </div>

      {entry.user_notes.length > 0 && (
        <ul className="gpj-directory-notes">
          {entry.user_notes.map((note) => <li key={note}>{note}</li>)}
        </ul>
      )}

      <div className="gpj-directory-actions">
        <a
          className="gpj-btn gpj-btn--sm gpj-btn--primary"
          href={entry.public_website_url}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open official service <span aria-hidden="true">↗</span>
        </a>
        {entry.official_source_url !== entry.public_website_url && (
          <a
            className="gpj-directory-source-link"
            href={entry.official_source_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            View verification source
          </a>
        )}
      </div>

      <p className="gpj-directory-verified">
        Source: {entry.source_name} · verified {verificationDate(entry.source_verified_at)}
      </p>
    </article>
  );
}

export default function Directory() {
  const [response, setResponse] = useState(null);
  const [query, setQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [entryType, setEntryType] = useState('');
  const [category, setCategory] = useState('');
  const [region, setRegion] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const result = await directoryAPI.search({
          q: appliedQuery || null,
          entryType: entryType || null,
          category: category || null,
          region: region || null,
          limit: 50
        });
        if (active) setResponse(result.data);
      } catch (requestError) {
        if (active) {
          setError(safeError(requestError, 'Unable to load the Find Help directory.'));
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [appliedQuery, entryType, category, region]);

  const optionLabels = useMemo(() => Object.fromEntries(
    Object.values(response?.filter_options || {}).flat()
      .map((option) => [option.value, option.label])
  ), [response]);

  const applySearch = (event) => {
    event.preventDefault();
    setAppliedQuery(query.trim());
  };

  const clear = () => {
    setQuery('');
    setAppliedQuery('');
    setEntryType('');
    setCategory('');
    setRegion('');
  };

  const filters = response?.filter_options || {
    entry_types: [], categories: [], regions: []
  };

  return (
    <section className="gpj-section gpj-directory" aria-labelledby="directory-title">
      <div className="gpj-wrap">
        <div className="gpj-sec-head">
          <span className="gpj-eyebrow">Find Help directory</span>
          <h2 id="directory-title">Family law professionals and support services in Ontario</h2>
          <p>
            Search verified organization and referral resources. GPJ does not list or rank
            individual professionals in this starter catalogue.
          </p>
        </div>

        <aside className="gpj-directory-safety">
          <strong>Need urgent safety help?</strong>
          <p>
            If there is immediate danger, call 911 or local police. For other urgent safety
            concerns, use an official crisis or victim-support resource rather than waiting
            for a directory response.
          </p>
        </aside>

        <form className="gpj-directory-search" onSubmit={applySearch} role="search">
          <div className="gpj-directory-search__text">
            <label htmlFor="directory-search">Search the directory</label>
            <div>
              <input
                id="directory-search"
                type="search"
                value={query}
                maxLength={100}
                placeholder="Organization, service, or broad region"
                onChange={(event) => setQuery(event.target.value)}
              />
              <button type="submit" className="gpj-btn gpj-btn--primary">Search</button>
            </div>
          </div>

          <div className="gpj-directory-filters">
            <label>
              Type
              <select value={entryType} onChange={(event) => setEntryType(event.target.value)}>
                <option value="">All types</option>
                {filters.entry_types.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label>
              Service
              <select value={category} onChange={(event) => setCategory(event.target.value)}>
                <option value="">All services</option>
                {filters.categories.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label>
              Coverage
              <select value={region} onChange={(event) => setRegion(event.target.value)}>
                <option value="">All Ontario coverage</option>
                {filters.regions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <button type="button" className="gpj-btn gpj-btn--ghost" onClick={clear}>
              Clear
            </button>
          </div>
        </form>

        {error && <div className="gpj-error" role="alert">{error}</div>}
        {loading && <div className="gpj-loading" aria-live="polite">Loading verified listings…</div>}

        {!loading && !error && response && (
          <div className="gpj-directory-results" aria-live="polite">
            <div className="gpj-results-head">
              <h3>Directory listings</h3>
              <span className="gpj-result-count">
                {response.result_count} {response.result_count === 1 ? 'result' : 'results'}
              </span>
            </div>

            {response.entries.length > 0 ? (
              <div className="gpj-directory-grid">
                {response.entries.map((entry) => (
                  <DirectoryCard
                    key={entry.directory_entry_id}
                    entry={entry}
                    optionLabels={optionLabels}
                  />
                ))}
              </div>
            ) : (
              <div className="gpj-directory-empty">
                <h3>No verified listings match these filters</h3>
                <p>Try a broader service or coverage filter. No individual profile will be inferred.</p>
                <button type="button" className="gpj-btn gpj-btn--ghost" onClick={clear}>
                  Clear search and filters
                </button>
              </div>
            )}
          </div>
        )}

        <p className="gpj-disclaimer">
          <b>No endorsement or recommendation.</b> GPJ provides source-linked directory
          information only. Verify current availability, eligibility, scope, and fees directly
          with the organization or professional before relying on a listing.
        </p>
      </div>
    </section>
  );
}

